import type { User as GQLItem, UserConnection as GQLConnection } from '../../../generated/internal.graphql';
import { take } from '../../../util/takePayload';
import * as GqlConnection from '../../connection/GqlConnection';
import * as PageInfo from '../../connection/PageInfo';
import * as UserAccountMembership from './UserAccountMembership';

/**
 * A user as the staff lookup screen reads them: enough to recognise the person, plus the account
 * memberships that let an operator jump to the account they are calling about.
 *
 * `User.active` — the backend's "has this user ever signed in" support signal — is deliberately
 * neither selected nor mapped. The screen this serves answers "which accounts is this person on",
 * and carrying a second signal would cost a column, a fixture field and a render branch for a
 * question nobody asked yet. Add it here and in `fragments.ts` together when someone does.
 */
export type User = {
  id: number;
  name: string;
  email: string;
  memberships: UserAccountMembership.UserAccountMembership[];
};

export type UserConnection = GqlConnection.GqlConnection<User>;

/**
 * `memberships` is non-null on the schema, so the fallback is not for today's resolver — it is the
 * same soft-failing posture the rest of this namespace takes, so a resolver that returns nothing
 * yields a user with no memberships rather than a "Cannot read properties of null" the caller can
 * only report as an opaque failure.
 */
export const fromGql = (item: GQLItem): User => ({
  ...take<User>(item, 'id', 'name', 'email'),
  memberships: (item.memberships ?? []).map(UserAccountMembership.fromGql),
});

/**
 * Mapped by hand rather than through `GqlConnection.fromGql`, for the two reasons
 * `Internal/TicketLayout` documents and that hold identically here:
 *
 * - `edges` and `node` are both nullable on the schema. graphql-ruby does not emit those nulls for
 *   this resolver, but casting them away would make the mapper throw out of an API documented as
 *   never throwing.
 * - The query selects `edges` only, so the shared helper would leave `nodes` permanently `[]` while
 *   `GqlConnection<T>` promises a populated array. Deriving it from the edges keeps that promise.
 */
export const fromGqlConnection = (connection: GQLConnection): UserConnection => {
  const edges = (connection.edges ?? [])
    .filter(edge => Boolean(edge?.node))
    .map(edge => ({ cursor: edge!.cursor, node: fromGql(edge!.node as GQLItem) }));

  return {
    edges,
    nodes: edges.map(edge => edge.node),
    pageInfo: PageInfo.fromGql(connection.pageInfo),
  };
};
